import { VoicemailGreetingType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { RouteType } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { buildAudioLibraryPath } from '../../../utils/media-path';
import { AudioQueryMedia } from '../../../utils/use-audio-query';
import { findMediaItem } from '../../audio-picker/audio-picker';
import { CanvasPhoneTree, getDialOptionChildren } from '../../call-routes/helpers/graph/node-utils';
import {
  DialOptionData,
  DialOptionInstruction,
  PlayInstruction,
  VoicemailInstruction,
  VoicemailPromptPayload,
} from '../../phone-tree-quick-actions/types';

// This function traverses the phone tree graph and converts it into an array of DialOptionData.
export const transformGraphToDialOptions = ({
  nodes,
  edges,
  phoneTreeNodeId,
  audioFiles,
}: {
  nodes: CanvasPhoneTree['nodes'];
  edges: CanvasPhoneTree['edges'];
  phoneTreeNodeId: string;
  audioFiles: AudioQueryMedia;
}): DialOptionData[] => {
  const dialOptionNodes = getDialOptionChildren(phoneTreeNodeId, nodes, edges);
  return dialOptionNodes
    .map((optionNode) => {
      const number = parseInt(optionNode.callObject.primitiveName, 10);
      const routes: DialOptionInstruction[] = [];

      let currentNode: CallRouteV1.Types.Node | undefined = optionNode;

      // Traverse the graph to find the sequence of routes
      // The first step is the child of the TreeOptionNode
      const firstEdge = edges.find((e) => e.sourceId === optionNode.id);
      if (firstEdge) {
        currentNode = nodes.find((n) => n.id === firstEdge.targetId);
      } else {
        currentNode = undefined;
      }

      while (currentNode) {
        const nodeId = currentNode.id;
        // Skip Fallback nodes, but follow their edges
        if (currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK) {
          const nextEdge = edges.find((e) => e.sourceId === nodeId);
          currentNode = nextEdge ? nodes.find((n) => n.id === nextEdge.targetId) : undefined;
          continue;
        }

        const routeType = mapNodeTypeToRouteType(currentNode.type);
        if (routeType !== RouteType.ROUTE_TYPE_UNSPECIFIED) {
          routes.push(createPayload(currentNode, audioFiles));
        }

        if (
          currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE ||
          currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS
        ) {
          // Stop traversal at Phone Tree nodes to avoid going into nested trees.
          break;
        }

        // Check for fallback/next step
        const nextEdge = edges.find((e) => e.sourceId === nodeId);
        if (nextEdge) {
          currentNode = nodes.find((n) => n.id === nextEdge.targetId);
        } else {
          currentNode = undefined;
        }
      }

      return {
        number,
        readOnly: true,
        routes,
      };
    })
    .sort((a, b) => a.number - b.number);
};

const mapNodeTypeToRouteType = (nodeType: CallRouteV1.Types.NodeType): RouteType => {
  switch (nodeType) {
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP:
      return RouteType.ROUTE_TYPE_CALL_GROUP;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE:
      return RouteType.ROUTE_TYPE_CALL_QUEUE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE:
    case CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION:
      return RouteType.ROUTE_TYPE_CALL_ROUTE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE:
      return RouteType.ROUTE_TYPE_DEVICE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER:
      return RouteType.ROUTE_TYPE_FORWARDING_NUMBER;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE:
      return RouteType.ROUTE_TYPE_IVR_MENU;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE:
      return RouteType.ROUTE_TYPE_PLAY_MESSAGE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT:
      return RouteType.ROUTE_TYPE_REPEAT;
    case CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX:
      return RouteType.ROUTE_TYPE_VOICEMAIL;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS:
      return RouteType.ROUTE_TYPE_PHONE_HOURS;
    case CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT:
      return RouteType.ROUTE_TYPE_WEAVE_ASSISTANT;
    default:
      return RouteType.ROUTE_TYPE_UNSPECIFIED;
  }
};

const createPayload = (node: CallRouteV1.Types.Node, audioFiles: AudioQueryMedia): DialOptionInstruction => {
  const { callObject } = node;
  const routeType = mapNodeTypeToRouteType(node.type);

  switch (node.type) {
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP:
      if ('callGroupExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Call Group',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE:
      if ('callQueueExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Call Queue',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE:
    case CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION:
      if ('callRouteExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Call Route',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE:
      if ('deviceExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Device',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER:
      if ('forwardingNumberExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Forwarding Number',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE:
      if ('phoneTreeExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Phone Tree',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE:
      if ('playMessageExpansion' in callObject) {
        const mediaItemId = callObject.playMessageExpansion.mediaItemId;
        let filePath = '';
        const media = findMediaItem(mediaItemId, audioFiles?.allMedia || []);
        if (media) {
          filePath = buildAudioLibraryPath({ media });
        }
        return {
          type: routeType,
          id: mediaItemId,
          name: callObject.primitiveName || 'Unknown Play Message',
          data: {
            filePath,
          },
        } as PlayInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX:
      if ('voicemailExpansion' in callObject) {
        const data: VoicemailPromptPayload = {
          voicemailBoxId: callObject.primitiveId,
          // this is the default greeting case: skipGreeting is false and none of the other fields are set.
          skipGreeting: false,
          systemMediaId: '',
          voicemailGreetingId: '',
        };

        if (callObject.voicemailExpansion.greetingType === VoicemailGreetingType_Enum.NO_GREETING) {
          data.skipGreeting = true;
        } else if (callObject.voicemailExpansion.greetingType === VoicemailGreetingType_Enum.NUMBERED_GREETING) {
          data.voicemailGreetingId = callObject.voicemailExpansion.greetingMediaItemId;
        } else if (callObject.voicemailExpansion.greetingType === VoicemailGreetingType_Enum.AUDIO_LIBRARY_GREETING) {
          data.systemMediaId = callObject.voicemailExpansion.greetingMediaItemId;
        }
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Voicemail Box',
          data,
        } as VoicemailInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS:
      if ('phoneHoursExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Phone Hours',
        } as DialOptionInstruction;
      }
      break;
  }

  // Fallback for unhandled types
  return {
    type: routeType,
    id: callObject.primitiveId || '',
    name: callObject.primitiveName || 'Unknown',
  } as DialOptionInstruction;
};
